import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";
import { pricingTiers } from "./homeData";

export function PricingSection({ ctaLabel, onCta }) {
  return (
          <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 lg:pb-20 scroll-mt-20">
            <div className="text-center mb-8">
              <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">Pricing</span>
              <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>Simple, Transparent Pricing</h2>
              <p className="mt-2 text-sm text-slate-500">No hidden fees · Pay per consultation · GST included</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4" data-testid="pricing-tiers">
              {pricingTiers.map((t) => (
                <div
                  key={t.title}
                  data-testid={`pricing-${t.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`relative rounded-2xl p-6 border-2 transition-all ${t.popular ? "border-[#D4AF37] bg-[#FBF4DF]/40 shadow-xl" : "border-slate-200 bg-white hover:border-[#D4AF37] hover:shadow-lg"}`}
                >
                  {t.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#0A2540] text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                      Most popular
                    </span>
                  )}
                  <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${t.accent}20`, color: t.accent }}>
                    <t.icon size={22} />
                  </div>
                  <h3 className="mt-4 text-2xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{t.title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{t.sub}</p>
                  <p className="mt-3 text-3xl font-extrabold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>{t.range}</p>
                  <ul className="mt-4 space-y-2">
                    {t.bullets.map((b) => (
                      <li key={b} className="flex items-center gap-2 text-sm text-slate-700">
                        <ShieldCheck size={14} className="text-emerald-600 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>
                  <Link to="/lawyers" onClick={onCta}>
                    <Button className={`mt-5 w-full rounded-full font-bold h-10 ${t.popular ? "adv-btn-gold" : "adv-btn-primary"}`}>
                      {ctaLabel}
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </section>
  );
}

export function HowItWorksSection({ ctaLabel, onCta }) {
  return (
          <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 scroll-mt-20">
            <div className="rounded-3xl adv-navy text-white p-8 lg:p-12 relative overflow-hidden">
              <div className="adv-blob bg-[#D4AF37]" style={{ width: 400, height: 400, top: -180, right: -100, opacity: 0.25 }} />
              <div className="relative">
                <span className="text-xs font-bold uppercase tracking-widest text-[#D4AF37]">How it works</span>
                <h2 className="mt-2 text-3xl lg:text-4xl font-extrabold" style={{ fontFamily: "Poppins" }}>Three simple steps</h2>
                <div className="mt-8 grid md:grid-cols-3 gap-6">
                  {[
                    { n: "01", t: "Describe your problem", d: "Tell us your issue in 30 seconds — no legal jargon needed." },
                    { n: "02", t: "Chat with verified lawyers", d: "Connect instantly with lawyers who specialise in your matter." },
                    { n: "03", t: "Pay only if you want to continue", d: "Start with a free AI guide. Upgrade to a paid consult only when ready." },
                  ].map((s) => (
                    <div key={s.n} className="relative">
                      <div className="text-5xl font-black text-[#D4AF37]/40" style={{ fontFamily: "Poppins" }}>{s.n}</div>
                      <h3 className="mt-2 text-xl font-bold" style={{ fontFamily: "Poppins" }}>{s.t}</h3>
                      <p className="mt-1 text-sm text-white/70 leading-relaxed">{s.d}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex items-center gap-3 flex-wrap">
                  <Link to="/lawyers" onClick={onCta}>
                    <Button data-testid="how-cta-consult" className="rounded-full adv-btn-gold font-bold h-11 px-6">{ctaLabel}</Button>
                  </Link>
                  <Link to="/chat">
                    <Button variant="outline" data-testid="how-cta-ai" className="rounded-full h-11 px-6 border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white font-semibold">
                      Get Quick Legal Guidance (Free)
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </section>
  );
}
